/**
 * User Plan Service
 * 
 * Service to retrieve current user plan information and remaining inventory count.
 */

import {
  useGetUserInfoQuery,
  useGetChargesQuery,
  useGetPlansQuery,
  useGetInventoriesQuery,
  GetChargesApiResponse,
  GetPlansApiResponse,
  GetUserInfoApiResponse,
  GetInventoryApiResponse,
} from 'src/drheranca';

/**
 * Type definition for the user plan information
 */
export interface UserPlanInfo {
  userName: string;
  isPlanActive: boolean;
  currentActivePlanName: string;
  currentActivePlanID: string;
  maxInventories: number;
  lastChargeValue: number;
  charges: GetChargesApiResponse['list'];
  createdInventories: number;
}

/**
 * Processes the user plan information from API responses
 * 
 * @param userInfo - User information from getUserInfo API
 * @param chargesResponse - Charges response from getCharges API
 * @param plansResponse - Plans response from getPlans API
 * @param inventoriesResponse - Inventories response from getInventories API
 * @returns UserPlanInfo object with processed data
 */
export function processUserPlanInfo(
  userInfo: GetUserInfoApiResponse | undefined,
  chargesResponse: GetChargesApiResponse | undefined,
  plansResponse: GetPlansApiResponse | undefined,
  inventoriesResponse: GetInventoryApiResponse | undefined
): UserPlanInfo {
  // Default values
  const defaultInfo: UserPlanInfo = {
    userName: '',
    isPlanActive: false,
    currentActivePlanName: '',
    currentActivePlanID: '',
    maxInventories: 0,
    lastChargeValue: 0,
    charges: [],
    createdInventories: 0,
  };

  // Fill userName from userInfo
  if (userInfo?.user?.userName) {
    defaultInfo.userName = userInfo.user.userName;
  }

  // Fill charges with all charges found
  if (chargesResponse?.list) {
    defaultInfo.charges = chargesResponse.list;
  }

  // Count created inventories
  if (inventoriesResponse?.list) {
    defaultInfo.createdInventories = inventoriesResponse.list.length;
  }

  // Filter charges where status = 'ACTIVE'
  const activeCharges = defaultInfo.charges.filter(
    (charge) => charge.status === 'ACTIVE'
  );

  // If no active charges, return default info
  if (activeCharges.length === 0) {
    return defaultInfo;
  }

  // Sort by accessionDate descending (most recent first)
  const sortedActiveCharges = [...activeCharges].sort((a, b) => {
    const dateA = new Date(a.accessionDate).getTime();
    const dateB = new Date(b.accessionDate).getTime();
    return dateB - dateA; // Descending order
  });

  // Get the first (most recent) active charge
  const mostRecentActiveCharge = sortedActiveCharges[0];

  // Fill plan active information
  defaultInfo.isPlanActive = true;
  defaultInfo.lastChargeValue = mostRecentActiveCharge.priceValue;
  defaultInfo.currentActivePlanID = mostRecentActiveCharge.planoId;
  defaultInfo.currentActivePlanName = mostRecentActiveCharge.planoName;

  // Find the plan by planoId to get maxInventories
  if (plansResponse?.list) {
    const plan = plansResponse.list.find(
      (p) => p.id === mostRecentActiveCharge.planoId
    );

    if (plan) {
      defaultInfo.maxInventories = plan.maxInventories;
    }
  }

  return defaultInfo;
}

/**
 * Custom hook to get user plan information
 * 
 * This hook automatically fetches user info, charges, plans, and inventories,
 * then processes them to return the user plan information.
 * 
 * @returns Object containing userPlanInfo, loading states, and error states
 */
export function useUserPlanInfo() {
  // Fetch user info
  const {
    data: userInfo,
    isLoading: isLoadingUserInfo,
    isError: isErrorUserInfo,
  } = useGetUserInfoQuery(null, {
    refetchOnMountOrArgChange: true,
  });

  // Fetch all charges
  const {
    data: chargesResponse,
    isLoading: isLoadingCharges,
    isError: isErrorCharges,
  } = useGetChargesQuery(
    {
      offset: 0,
      maxSize: 200, // Get all charges
      orderBy: 'accessionDate',
      order: 'desc',
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  // Fetch all plans
  const {
    data: plansResponse,
    isLoading: isLoadingPlans,
    isError: isErrorPlans,
  } = useGetPlansQuery(
    {
      offset: 0,
      maxSize: 200, // Get all plans
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  // Fetch all inventories
  const {
    data: inventoriesResponse,
    isLoading: isLoadingInventories,
    isError: isErrorInventories,
  } = useGetInventoriesQuery(
    {
      offset: 0,
      maxSize: 200, // Get all inventories
      orderBy: 'createdAt',
      order: 'desc',
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  // Process the data
  const userPlanInfo = processUserPlanInfo(
    userInfo,
    chargesResponse,
    plansResponse,
    inventoriesResponse
  );

  // Combined loading state
  const isLoading =
    isLoadingUserInfo || isLoadingCharges || isLoadingPlans || isLoadingInventories;

  // Combined error state
  const isError = isErrorUserInfo || isErrorCharges || isErrorPlans || isErrorInventories;

  return {
    userPlanInfo,
    isLoading,
    isError,
  };
}

